import { BANK, type SkillId } from './bank';
import { type ExtraQuestion, type InterviewPack, PACKS, type PackKey } from './packs';
import { PAGES } from './pages';

/**
 * PAGE GROUNDING: each guide page's interview is built from that page's own article.
 *
 * Jay, Sep 23: "Is the interview module actually reading the content on the blog?" It was not: four
 * generic packs with the company name swapped in. On a guide whose article is about software
 * engineering, the data pack asked about a DAU drop the article never mentions. 65 of 200 pages were
 * pulled for that.
 *
 * Now each page's two skill rounds come from the top of ITS OWN "What Skills Does X Look For" chart
 * (mapped to the skill bank), the card cites ITS OWN interview-process section, and the externship at
 * the end is the one that fits the role family the article describes. `pages.ts` is generated from
 * the live articles (see the grounding folder in the Labs working directory) and reviewed before it
 * ships. A page with no entry falls back to its pack, exactly as before.
 */

export type ExternshipKey = 'amazon-ops' | 'attronica' | 'beats' | 'bereal-pm' | 'breaking-games' | 'wayfair-ai';

export type RoleFamily =
  | 'accounting-firm'
  | 'ai-ml'
  | 'consulting'
  | 'data-analytics'
  | 'finance'
  | 'marketing'
  | 'operations'
  | 'other'
  | 'product'
  | 'software';

export interface PageGrounding {
  family: RoleFamily;
  /** One sentence from the article's interview-process section. Null when the article has none. */
  processLine: string | null;
  /** The article's top two skills, as bank ids, most important first. */
  skills: readonly [SkillId, SkillId];
}

/** The real, live externships a scorecard can bridge to. Module steps are the programs' own titles. */
export const EXTERNSHIPS: Record<ExternshipKey, InterviewPack['ext']> = {
  'amazon-ops': PACKS.consulting.ext,
  attronica: PACKS.banking.ext,
  beats: PACKS.marketing.ext,
  'bereal-pm': {
    color: '#111111',
    externshipId: 676,
    line: 'For BeReal I mapped the user journey, prioritized a new feature with RICE scoring, prototyped it, and tested it with a focus group before pitching it.',
    mark: 'BR',
    org: 'BeReal',
    steps: [
      'Map the end-to-end user journey from real user data',
      'Benchmark competitors and pick a feature with RICE scoring',
      'Write a lite PRD, prototype it, and test it in a focus group',
    ],
    title: 'BeReal Product Management',
  },
  'breaking-games': PACKS.data.ext,
  'wayfair-ai': {
    color: '#7b189f',
    externshipId: 665,
    line: 'I built AI agents in n8n for a Wayfair project, including one that collects competitor data, analyzes it, and delivers a report. I can walk you through how the pipeline works.',
    mark: 'W',
    org: 'Wayfair',
    steps: [
      'Build your first AI agent in n8n',
      'Build a trend-discovery agent that gathers and analyzes real market signals',
      'Build a competitor-monitoring agent and ship a final dashboard agent',
    ],
    title: 'Wayfair n8n AI Agent Engineering',
  },
};

const FAMILY_EXTERNSHIP: Record<RoleFamily, ExternshipKey | null> = {
  'accounting-firm': 'attronica',
  'ai-ml': 'wayfair-ai',
  consulting: 'amazon-ops',
  'data-analytics': 'breaking-games',
  finance: 'attronica',
  marketing: 'beats',
  operations: 'amazon-ops',
  other: null,
  product: 'bereal-pm',
  software: 'wayfair-ai',
};

/**
 * For an article with no clear role family ("other": retail, hospitality, media, sports), the bridge
 * follows the page's own lead skill instead of its old pack. The Sep 23 audit found Hyatt, whose rounds
 * are SQL and case structure, bridging to a creative-advertising externship.
 */
const SKILL_EXTERNSHIP: Partial<Record<SkillId, ExternshipKey>> = {
  accounting: 'attronica',
  'case-structure': 'amazon-ops',
  'coding-fundamentals': 'wayfair-ai',
  'consumer-insight': 'beats',
  excel: 'breaking-games',
  experimentation: 'breaking-games',
  'financial-modeling': 'attronica',
  'market-sizing': 'amazon-ops',
  'marketing-analytics': 'beats',
  'marketing-strategy': 'beats',
  markets: 'attronica',
  'ml-fundamentals': 'wayfair-ai',
  operations: 'amazon-ops',
  'product-metrics': 'breaking-games',
  'product-sense': 'bereal-pm',
  'quant-reasoning': 'breaking-games',
  research: 'beats',
  'sql-data': 'breaking-games',
  statistics: 'breaking-games',
  'supply-chain': 'amazon-ops',
  valuation: 'attronica',
};

function externshipFor(page: PageGrounding): ExternshipKey | null {
  const byFamily = FAMILY_EXTERNSHIP[page.family];
  if (byFamily) {
    return byFamily;
  }
  const lead = page.skills.find((id) => id !== 'communication');
  return lead ? (SKILL_EXTERNSHIP[lead] ?? null) : null;
}

/** Whose story question and whose fallback content a family borrows. */
const FAMILY_PACK: Record<RoleFamily, PackKey | null> = {
  'accounting-firm': 'banking',
  'ai-ml': 'data',
  consulting: 'consulting',
  'data-analytics': 'data',
  finance: 'banking',
  marketing: 'marketing',
  operations: 'consulting',
  other: null,
  product: 'data',
  software: 'data',
};

const EXTRA_COUNT = 7;

function modelAnswer(question: InterviewPack['qs'][number]): ExtraQuestion {
  return { model: `${question.options[question.correct] ?? ''}. ${question.why}`, q: question.prompt };
}

/**
 * The pack a page actually runs: its own two skill rounds, its family's story and externship, and
 * practice questions drawn from its own skills first. Pure, and shared by the app and the guide-page
 * card, so the question on the page is always the question the interview opens on.
 */
export function groundedPack(packKey: PackKey, page: PageGrounding | undefined): InterviewPack {
  const base = PACKS[packKey];
  if (!page) {
    return base;
  }
  const [first, second] = page.skills;
  const firstQuestion = BANK[first].questions[0];
  // Two rounds on one skill would repeat a topic; use its second question if it has one.
  const secondQuestion = first === second ? BANK[second].questions[1] : BANK[second].questions[0];
  if (!(firstQuestion && secondQuestion)) {
    return base;
  }
  const storyPack = PACKS[FAMILY_PACK[page.family] ?? packKey];
  const externshipKey = externshipFor(page);
  const bankExtras = [first, second]
    .flatMap((id) => BANK[id].questions)
    .filter((question) => question !== firstQuestion && question !== secondQuestion)
    .map(modelAnswer);
  const extra = [...bankExtras, ...storyPack.extra].slice(0, EXTRA_COUNT);
  return {
    ...base,
    dims: [BANK[first].label, BANK[second].label, 'Story evidence'],
    ext: externshipKey ? EXTERNSHIPS[externshipKey] : base.ext,
    extra,
    qs: [firstQuestion, secondQuestion],
    story: storyPack.story,
    track: base.track,
  };
}

export function pageGrounding(guideSource: string): PageGrounding | undefined {
  return Object.hasOwn(PAGES, guideSource) ? PAGES[guideSource] : undefined;
}
